import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import {
  Grid,
  Typography,
  Box,
  TablePagination,
  IconButton,
  Modal,
  Divider,
} from "@mui/material";
import { Visibility, Close } from "@mui/icons-material";
import { API_BASE_URL } from "../../config/constants";
import { indianStates } from "../../config/constantselections";

import CustomDatePicker from "./../../utility-v1/CustomDatePicker";
import * as XLSX from "xlsx";

const AnalyticsPage = () => {
  const [profitStartDate, setProfitStartDate] = useState("");
  const [profitEndDate, setProfitEndDate] = useState("");
  const [salesData, setSalesData] = useState([]);
  const [profitStats, setProfitStats] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setDefaultDateRanges();
  }, []);

const GSTIN_ECOM =  "";
const exportSalesToExcel = () => {
  if (!salesData?.length) return;

  // 👉 Sort by sales_date (ascending)
  const sortedSales = [...salesData].sort(
    (a, b) => new Date(a.sales_date) - new Date(b.sales_date)
  );

  const rows = sortedSales.map((s) => {
    const price = Number(s.total_price || 0);
    const shipping = Number(s.shipment_price || 0);

    const purchaseCost = (s.items || []).reduce(
      (sum, it) =>
        sum + Number(it.purchase_price || 0) * Number(it.quantity || 0),
      0
    );

    const profit = price - purchaseCost - shipping - 80;

    return {
      "Customer Name": s.name || "",
      "Invoice No": s.sales_id,
      "Invoice Date (dd/mm/yyyy)": moment(s.sales_date).format("DD/MM/YYYY"),
      "Invoice Value": price,
      "GST%": "",
      "Taxable Value": Number(profit.toFixed(2)), // profit placed in Taxable Value
      "CESS": "",
      "Place of supply": getStateName(s.state),
      "GSTIN of E commerce Operator": GSTIN_ECOM,
    };
  });

  const headerOrder = [
    "Customer Name",
    "Invoice No",
    "Invoice Date (dd/mm/yyyy)",
    "Invoice Value",
    "GST%",
    "Taxable Value",
    "CESS",
    "Place of supply",
    "GSTIN of E commerce Operator",
  ];

  const ordered = rows.map((r) => {
    const o = {};
    headerOrder.forEach((h) => (o[h] = r[h]));
    return o;
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(ordered, { header: headerOrder });
  XLSX.utils.book_append_sheet(wb, ws, "Sales");

  const from = profitStartDate || "from";
  const to = profitEndDate || "to";
  XLSX.writeFile(wb, `Sales_${from}_to_${to}.xlsx`);
};

const getStateName = (code) => {
  const match = indianStates.find((s) => s.value === code);
  return match ? match.label : code; // fallback to code if not found
};





  const setDefaultDateRanges = () => {
    const today = new Date();
    const last30Days = new Date(today);
    last30Days.setDate(today.getDate() - 30);

    setProfitStartDate(moment(last30Days).format("YYYY-MM-DD"));
    setProfitEndDate(moment(today).format("YYYY-MM-DD"));
  };

  useEffect(() => {
    if (profitStartDate && profitEndDate) {
      fetchSalesReport(profitStartDate, profitEndDate, "SD");
    }
  }, [profitStartDate, profitEndDate]);

const fetchSalesReport = (fromDate, toDate, status) => {
  axios
    .get(`${API_BASE_URL}/salesreport`, {
      params: { from: fromDate, to: toDate, status }, // ✅ pass as query params
    })
    .then((response) => {
      groupSalesById(response.data);
      calculateProfit(response.data);
    })
    .catch((error) => {
      console.error("Error fetching sales data:", error);
    });
};


const groupSalesById = (sales) => {
  const groupedSales = sales.reduce((acc, sale) => {
    if (!acc[sale.sales_id]) {
      acc[sale.sales_id] = {
        sales_id: sale.sales_id,
        sales_date: sale.sales_date,
        total_price: Number(sale.total_price) || 0,
        name: sale.name || "",
        buyer_details: sale.buyer_details || "",
        pincode: sale.pincode || "",
        state: sale.state || "",
        phone_number: sale.phone_number || "",
        shipment_price: isNaN(Number(sale.shipment_price))
          ? 0
          : Number(sale.shipment_price),
        total_purchase_cost: 0,  // 👈 we’ll fill this
        items: [],
      };
    }

    const qty = Number(sale.quantity) || 0;
    const purchasePrice = Number(sale.purchase_price) || 0;

    // accumulate purchase cost
    acc[sale.sales_id].total_purchase_cost += purchasePrice * qty;

    acc[sale.sales_id].items.push({
      item_label: sale.item_label,
      quantity: qty,
      purchase_price: purchasePrice,
    });

    return acc;
  }, {});

  setSalesData(Object.values(groupedSales));
};

  const calculateProfit = (sales) => {
    let totalProfit = 0;
    let totalRevenue = 0;
    let totalCost = 0;
    let totalQuantity = 0;
    let totalShipment = 0;
    let saleCount = 0;
    let boxPrice = 0;

    const groupedSales = sales.reduce((acc, sale) => {
      if (!acc[sale.sales_id]) {
        acc[sale.sales_id] = {
          total_price: parseFloat(sale.total_price),
          shipment_price: isNaN(parseFloat(sale.shipment_price)) ? 0 : parseFloat(sale.shipment_price), // ✅ Ensure shipment_price is 0 if null
          quantity: 0,
          total_cost: 0,
        };
      }

      acc[sale.sales_id].quantity += parseFloat(sale.quantity);
      acc[sale.sales_id].total_cost += parseFloat(sale.purchase_price) * parseFloat(sale.quantity);

      return acc;
    }, {});

    Object.values(groupedSales).forEach((sale) => {
      totalRevenue += sale.total_price;
      totalCost += sale.total_cost;
      totalQuantity += sale.quantity;
      totalShipment += sale.shipment_price;
    });

    saleCount = Object.keys(groupedSales).length;
    totalProfit = totalRevenue - totalCost - totalShipment;
    boxPrice = saleCount * 20;

    let profitPercentage = totalCost > 0 ? ((totalProfit / totalCost) * 100).toFixed(2) : 0;

    setProfitStats({
      totalProfit,
      profitPercentage,
      totalQuantity,
      totalRevenue,
      totalShipment,
      saleCount,
      boxPrice,
      totalCost
    });
};


  // Open Modal with Selected Sale Details
  const handleOpenModal = (sale) => {
    setSelectedSale(sale);
    setModalOpen(true);
  };

  // Close Modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSale(null);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Date Range Selection */}
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center">
        <Grid item xs={6} sm={6}>
          <CustomDatePicker
            label="Start Date"
            value={profitStartDate}
            onChange={(e) => setProfitStartDate(e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={6}>
          <CustomDatePicker
            label="End Date"
            value={profitEndDate}
            onChange={(e) => setProfitEndDate(e.target.value)}
          />
        </Grid>
      </Grid>
<Box sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%" }}>
      <button
        onClick={exportSalesToExcel}
        style={{
          padding: "8px 14px",
          borderRadius: 8,
          border: "1px solid #1976d2",
          background: "#1976d2",
          color: "#fff",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Export to Excel
      </button>
    </Box>
      {/* Profit Summary Boxes */}
      {profitStats && (
        <Grid container spacing={2} sx={{ mb: 3 }} justifyContent="center">
          <Grid item xs={6} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#d4edda",
                position: "relative",
                padding: 3,
                borderRadius: 2,
              }}
            >
              {/* Label positioned at top-left */}
              <Typography
                sx={{
                  position: "absolute",
                  top: 5,
                  left: 10,
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
               Profit
              </Typography>
              {/* Value centered below */}
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "22px",
                  textAlign: "center",
                  mt: 3,
                }}
              >
                ₹{profitStats.totalProfit.toFixed(2)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#d4edda",
                position: "relative",
                padding: 3,
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  top: 5,
                  left: 10,
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                Profit %
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "22px",
                  textAlign: "center",
                  mt: 3,
                }}
              >
                {profitStats.profitPercentage}%
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#d4edda",
                position: "relative",
                padding: 3,
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  top: 5,
                  left: 10,
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                Total Sales
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "22px",
                  textAlign: "center",
                  mt: 3,
                }}
              >
                {profitStats.saleCount}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#d4edda",
                position: "relative",
                padding: 3,
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  top: 5,
                  left: 10,
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                Revenue
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "22px",
                  textAlign: "center",
                  mt: 3,
                }}
              >
                ₹{profitStats.totalRevenue.toFixed(2)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#d4edda",
                position: "relative",
                padding: 3,
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  top: 5,
                  left: 10,
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                Item Count
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "22px",
                  textAlign: "center",
                  mt: 3,
                }}
              >
                {profitStats.totalQuantity}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#d4edda",
                position: "relative",
                padding: 3,
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  top: 5,
                  left: 10,
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                Invested Amount
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "22px",
                  textAlign: "center",
                  mt: 3,
                }}
              >
                {profitStats.totalCost}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#cce5ff",
                position: "relative",
                padding: 3,
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  top: 5,
                  left: 10,
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                Shipment Price
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "22px",
                  textAlign: "center",
                  mt: 3,
                }}
              >
                ₹{profitStats.totalShipment.toFixed(2)}
              </Typography>
            </Box>
          </Grid>


        
          <Grid item xs={6} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#cce5ff",
                position: "relative",
                padding: 3,
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  top: 5,
                  left: 10,
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                Packing Price
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "22px",
                  textAlign: "center",
                  mt: 3,
                }}
              >
                {profitStats.boxPrice}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Pagination Controls on Top */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={salesData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="" // Removes the label
        sx={{
          marginTop: "-20px",
          marginLeft: "-20px",
          backgroundColor: "#fff",
          "& .MuiTablePagination-toolbar": {
            minHeight: "20px", // Compact height
            display: "flex",
            flexWrap: "nowrap", // Keep everything in one row
          },
          "& .MuiTablePagination-spacer": {
            display: "none", // Removes unnecessary spacing
          },
          "& .MuiTablePagination-selectLabel": {
            display: "none", // Hides the label completely
          },
          "& .MuiTablePagination-displayedRows": {
            fontSize: "16px",
            fontWeight: "bold",
            marginLeft: "10px", // Adjust spacing
          },
          "& .MuiSelect-select": {
            fontSize: "13px",
            padding: "4px 6px",
            minWidth: "50px",
          },
          "& .MuiIconButton-root": {
            fontSize: "18px",
            padding: "2px",
            margin: "0 4px", // Adjust spacing between buttons
          },
          "& .MuiTablePagination-actions": {
            display: "flex",
            alignItems: "center",
            gap: "10px", // Adjust spacing between icons
          },
        }}
      />

      {/* Sales Records */}
      <Grid container spacing={1}>
        {salesData.map((sale) => (
          <Grid item xs={12} key={sale.sales_id}>
            <Box
              sx={{
                backgroundColor: "#f8f9fa",
                padding: 2,
                mb: 1,
                borderRadius: 1,
                border: "1px solid #ccc",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={{ fontWeight: "bold" }}>{sale.name}</Typography>
                <Typography sx={{ fontWeight: "bold", color: "#888" }}>
                  {moment(sale.sales_date).format("DD/MM/YYYY")}
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  ₹{sale.total_price}
                </Typography>
                <IconButton onClick={() => handleOpenModal(sale)}>
                  <Visibility />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Modal for Sales Details */}
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            backgroundColor: "white",
            p: 3,
            maxWidth: 400,
            margin: "auto",
            mt: 5,
            borderRadius: 2,
          }}
        >
          <IconButton onClick={handleCloseModal} sx={{ float: "right" }}>
            <Close />
          </IconButton>
          <Typography variant="h6">{selectedSale?.name}</Typography>
          <Divider sx={{ my: 1 }} />
          <Typography sx={{ fontWeight: "bold" }}>Buyer Details:</Typography>
          <Typography>{selectedSale?.buyer_details}</Typography>
          <Typography>
            {selectedSale?.state}, {selectedSale?.pincode}
          </Typography>
          <Typography>
            Shipment Price: ₹{selectedSale?.shipment_price}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography sx={{ fontWeight: "bold" }}>Items:</Typography>
          {selectedSale?.items.map((item, index) => (
            <Typography key={index}>
              - {item.item_label} ({item.quantity} pcs)
            </Typography>
          ))}
        </Box>
      </Modal>
    </Box>
  );


};

export default AnalyticsPage;
